//
// # agentActivity
//
// Phase model behind the AI Assistant's "is an agent working?" indicator.
//
// `AiAssistant` owns a `working` boolean driven by the live agent tree. That is
// enough to render a spinner, but not enough to render a good indicator: when a
// run ends, `working` simply goes false and anything keyed off it disappears.
// A user who looks away for ten seconds cannot tell whether the agent finished
// or never started.
//
// So the UI is keyed off three phases instead. `done` is a real, visible state
// that holds briefly after a run and then decays to `idle`, and `idle` renders
// as an explicit resting state rather than as nothing at all.
//

import { useEffect, useRef, useState } from "react";

export type AgentPhase = "idle" | "working" | "done";

// How long the completion acknowledgement stays up before decaying to `idle`.
// Long enough to notice on return from another window, short enough not to
// linger as stale status.
export const DONE_PHASE_MS = 2600;

export interface AgentActivity {
  readonly phase: AgentPhase;
  // Wall-clock ms when the current (or most recent) run started; null before
  // the first run. Drives the elapsed-time readout.
  readonly startedAt: number | null;
}

const IDLE: AgentActivity = { phase: "idle", startedAt: null };

// Derives the phase from the caller's `working` flag, holding `done` for
// DONE_PHASE_MS on the working -> not-working edge.
export function useAgentActivity(working: boolean): AgentActivity {
  const [activity, setActivity] = useState<AgentActivity>(IDLE);
  // Tracks whether the previous run was ever observed working, so that a
  // not-working push before anything started does not flash a spurious `done`.
  const wasWorking = useRef(false);

  useEffect(() => {
    if (working) {
      wasWorking.current = true;
      setActivity({ phase: "working", startedAt: Date.now() });
      return;
    }
    if (!wasWorking.current) return;
    wasWorking.current = false;
    setActivity((prev) => ({ phase: "done", startedAt: prev.startedAt }));
    const id = setTimeout(() => {
      setActivity((prev) => ({ phase: "idle", startedAt: prev.startedAt }));
    }, DONE_PHASE_MS);
    return () => {
      clearTimeout(id);
    };
  }, [working]);

  return activity;
}

// Elapsed whole seconds for the current run; 0 unless one is in flight.
// Re-renders once a second — a number that visibly ticks is a cheap and
// unambiguous "still going", with no extra animation.
export function useElapsedSeconds(activity: AgentActivity): number {
  const [seconds, setSeconds] = useState(0);
  const { phase, startedAt } = activity;
  useEffect(() => {
    if (phase !== "working" || startedAt === null) {
      setSeconds(0);
      return;
    }
    const tick = () => {
      setSeconds(Math.floor((Date.now() - startedAt) / 1000));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => {
      clearInterval(id);
    };
  }, [phase, startedAt]);
  return seconds;
}
